'use client';

import { useEffect, useRef } from 'react';
import { usePushNotifications } from '@/lib/features/notifications/use-push-notifications';
import { useAuth } from '@/lib/integrations/supabase';
import * as Sentry from '@sentry/nextjs';

const AUTO_SUBSCRIBE_KEY = 'pwa_auto_subscribed';

/**
 * Detects if the app is running in PWA/standalone mode
 */
function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;

  // iOS Safari standalone mode
  const isIOSStandalone = (window.navigator as any).standalone === true;

  // Android/Chrome standalone mode
  const isStandaloneDisplay = window.matchMedia('(display-mode: standalone)').matches;

  // Android app mode
  const isAndroidApp = document.referrer.includes('android-app://');

  return isIOSStandalone || isStandaloneDisplay || isAndroidApp;
}

/**
 * Component that automatically subscribes users to push notifications
 * when they open the PWA. This runs silently without showing any UI.
 *
 * Behavior:
 * - Only runs in PWA/standalone mode
 * - Only runs once per device (tracked via localStorage)
 * - Automatically requests notification permission
 * - Automatically subscribes to push notifications if granted
 * - Silent operation - no UI shown
 */
export function AutoPushSubscription() {
  const { isSupported, permission, subscription, subscribe } = usePushNotifications();
  const { user, profile } = useAuth();
  const hasAttempted = useRef(false);

  useEffect(() => {
    // Prevent multiple attempts in the same session
    if (hasAttempted.current) return;

    const autoSubscribe = async () => {
      // Only run in browser environment
      if (typeof window === 'undefined') return;

      // Only run in PWA/standalone mode
      if (!isStandaloneMode()) return;

      // Only run if user is authenticated
      if (!user || !profile) return;

      // Only run if push notifications are supported
      if (!isSupported) return;

      // Skip if already subscribed
      if (subscription) return;

      // Check if permission is already denied - don't try again
      if (permission === 'denied') return;

      // Check if we've already attempted auto-subscription on this device
      const hasAutoSubscribed = localStorage.getItem(AUTO_SUBSCRIBE_KEY) === 'true';
      if (hasAutoSubscribed && permission === 'granted') {
        // Already auto-subscribed and has permission, but no active subscription
        // This can happen if the subscription expired - try to re-subscribe silently
        try {
          hasAttempted.current = true;
          await subscribe();
        } catch (error) {
          Sentry.captureException(error, {
            tags: { feature: 'auto-push-subscription', action: 're-subscribe' }
          });
        }
        return;
      }

      // Mark as attempted
      hasAttempted.current = true;

      // Small delay to ensure app is fully loaded and service worker is ready
      await new Promise(resolve => setTimeout(resolve, 2000));

      try {
        // Request permission and subscribe in one go
        await subscribe();

        // Mark as auto-subscribed regardless of outcome
        localStorage.setItem(AUTO_SUBSCRIBE_KEY, 'true');

        // Log success for analytics
        if (Notification.permission === 'granted') {
          Sentry.logger.info('Auto push subscription successful');
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'auto-push-subscription', action: 'initial-subscribe' }
        });
        // Still mark as attempted to prevent repeated attempts
        localStorage.setItem(AUTO_SUBSCRIBE_KEY, 'true');
      }
    };

    autoSubscribe();
  }, [user, profile, isSupported, permission, subscription, subscribe]);

  // This component doesn't render anything - it just auto-subscribes
  return null;
}
